import { useCurrentUser } from '@mp/hooks';
import React, { useEffect, useRef } from 'react';
import calendlyWidget from '@mp/utils/calendlyWidget';
import { config } from '@mp/config';
import { useCalendlySchedulingMutations } from './mutations';
import { LoadingState } from '@mp/pages/application/components/LoadingState';

type CalendlyParams = {
  name?: string;
  email?: string;
  a1?: string;
};

type PhoneScreeningSchedulerFormProps = {
  height?: string;
};

const schedulingCompleted = (e: MessageEvent) =>
  e.origin === 'https://calendly.com' && e.data.event === 'calendly.event_scheduled';

export const PhoneScreeningSchedulerForm = ({
  height = '650px',
}: PhoneScreeningSchedulerFormProps) => {
  const { currentUser, isLoading } = useCurrentUser();
  const { schedulingCompletedMutation } = useCalendlySchedulingMutations();
  const calendlyURL = config.calendlyAssignmentURL;
  const processedEventRef = useRef<string | null>(null);

  const buildCalendlyURL = (): string => {
    if (!currentUser) {
      return calendlyURL;
    }

    const { first_name, last_name, email, phone_number } = currentUser;
    const fullName = [first_name, last_name].filter(Boolean).join(' ').trim();
    const calendlyParams: CalendlyParams = {};

    calendlyParams.name = fullName;
    calendlyParams.email = email;
    calendlyParams.a1 = phone_number;

    const params = new URLSearchParams();

    Object.entries(calendlyParams).forEach(([key, value]) => {
      if (value) {
        params.append(key, value);
      }
    });

    const queryString = params.toString();
    return queryString ? `${calendlyURL}?${queryString}` : calendlyURL;
  };

  useEffect(() => {
    if (isLoading || !currentUser) {
      return;
    }
    calendlyWidget();
  }, [isLoading, currentUser]);

  useEffect(() => {
    const handleMessage = (e: MessageEvent) => {
      if (schedulingCompleted(e)) {
        if (processedEventRef.current === e.data.payload.event.uri) {
          return;
        }
        processedEventRef.current = e.data.payload.event.uri;
        const payload = {
          professional_screening: {
            calendly_scheduled_event_uri: e.data.payload.event.uri,
            calendly_scheduled_event_invitee_uri: e.data.payload.invitee.uri,
            reason: 're_screening',
            screening_type: 'phone',
          },
        };
        schedulingCompletedMutation(payload);
      }
    };
    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [schedulingCompletedMutation]);

  if (isLoading || !currentUser) {
    return <LoadingState />;
  }

  return (
    <div className="calendly-inline-widget" data-url={buildCalendlyURL()} style={{ height }} />
  );
};
